/** Clientes movil: lista del tenant activo + alta breve. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantIdOrNull, tenantScoped } from '@/config/queryClient';
import { commercialService, type ListResponse, type MobileClientDto } from '@/services/commercialService';
import { useMobileAuth } from '@/state/MobileAuthContext';

export type MobileClient = MobileClientDto;

export function useMobileClientsQuery(search = '', enabled = true) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  const trimmed = search.trim();
  const activeSearch = trimmed === '' ? undefined : trimmed;
  return useQuery<ListResponse<MobileClient>>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-clients', activeSearch ?? '')
        : ['tenant', 'no-context', 'mobile-clients', activeSearch ?? ''],
    queryFn: () =>
      commercialService.clients({
        page_size: 25,
        ...(activeSearch !== undefined ? { search: activeSearch } : {}),
      }),
    enabled: tenantId !== null && hasPermission('clients.view') && enabled,
    staleTime: 30_000,
  });
}

export function useMobileClientQuery(clientId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileClient>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-client', clientId ?? '')
        : ['tenant', 'no-context', 'mobile-client', clientId ?? ''],
    queryFn: () => commercialService.client(clientId!),
    enabled: tenantId !== null && hasPermission('clients.view') && clientId !== null,
    staleTime: 30_000,
  });
}

export function useCreateClientMutation() {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => commercialService.createClient(body),
    onSuccess: () => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-clients') });
      }
    },
  });
}
